import { useState, useEffect, useRef } from "react";

/** ms without a new decoded frame before the stream is considered frozen */
const FREEZE_THRESHOLD_MS = 3_500;
const POLL_INTERVAL_MS    = 1_000;

/**
 * Detects a frozen remote video stream by monitoring for stalled frames.
 *
 * Uses requestVideoFrameCallback (Chrome/Edge) when available; falls back to
 * comparing videoElement.currentTime across 1-second intervals.
 *
 * Only active when isActive=true. Set isActive=false when already in
 * audio-only mode, no track is present, or the room is disconnected.
 */
export function useVideoFreezeDetection(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  isActive: boolean,
): boolean {
  const [isFrozen, setIsFrozen]     = useState(false);
  const lastFrameAtRef              = useRef<number>(0);
  const rvfcHandleRef               = useRef<number>(0);
  // Only declare frozen after we've confirmed at least one decoded frame.
  // This prevents false positives during initial buffering / track attachment.
  const firstFrameSeenRef           = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!isActive || !video) {
      setIsFrozen(false);
      firstFrameSeenRef.current = false;
      return;
    }

    const isPlayable = () =>
      !video.paused &&
      !video.ended &&
      video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;

    const supportsRVFC =
      typeof (HTMLVideoElement.prototype as any).requestVideoFrameCallback === "function";

    if (supportsRVFC) {
      const onFrame = () => {
        firstFrameSeenRef.current = true;
        lastFrameAtRef.current    = performance.now();
        setIsFrozen(false);
        rvfcHandleRef.current = (video as any).requestVideoFrameCallback(onFrame);
      };
      rvfcHandleRef.current = (video as any).requestVideoFrameCallback(onFrame);

      const checkId = setInterval(() => {
        // Never fire before the first frame — avoids false positives on startup
        if (!firstFrameSeenRef.current || !isPlayable()) { setIsFrozen(false); return; }
        setIsFrozen(performance.now() - lastFrameAtRef.current > FREEZE_THRESHOLD_MS);
      }, POLL_INTERVAL_MS);

      return () => {
        clearInterval(checkId);
        (video as any).cancelVideoFrameCallback(rvfcHandleRef.current);
        firstFrameSeenRef.current = false;
        setIsFrozen(false);
      };
    }

    // ── Fallback: compare currentTime across intervals ───────────────────────
    let lastTime   = -1;
    let staleTicks = 0;
    const STALE_TICKS = Math.ceil(FREEZE_THRESHOLD_MS / POLL_INTERVAL_MS);

    const intervalId = setInterval(() => {
      if (!isPlayable()) { staleTicks = 0; setIsFrozen(false); return; }

      if (video.currentTime === lastTime) {
        // Only count stale ticks after the first real currentTime advance
        if (firstFrameSeenRef.current && ++staleTicks >= STALE_TICKS) setIsFrozen(true);
      } else {
        firstFrameSeenRef.current = true;
        staleTicks = 0;
        lastTime   = video.currentTime;
        setIsFrozen(false);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      firstFrameSeenRef.current = false;
      setIsFrozen(false);
    };
  }, [videoRef, isActive]);

  return isFrozen;
}
